import { useRef, useState, useEffect, RefObject } from 'react';

import Quill, { QuillOptions } from 'quill';
import ImageUploader from "quill-image-uploader";
import uploadFile from "app/shared-components/uploadFunction";
import {updateUserImage} from "app/store/userSlice";
import {stopLoading} from "app/store/utilSlice";
import {showMessage} from "app/store/fuse/messageSlice";
import getFileURL from "app/shared-components/util/getFileURL";

// Register the image uploader module
Quill.register("modules/imageUploader", ImageUploader);

// Ensure the default image blot is registered
const Image = Quill.import('formats/image');
Quill.register(Image, true);

const theme = 'snow';

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ align: [] }],

    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],

    [{ size: ['small', false, 'large', 'huge'] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['link', 'image'],
    [{ color: [] }, { background: [] }],

    ['clean'],
  ],
  clipboard: {
    matchVisual: false,
  },
};

const formats = [
  'bold',
  'italic',
  'underline',
  'strike',
  'align',
  'list',
  'indent',
  'size',
  'header',
  'link',
  'image',
  'video',
  'color',
  'background',
  'clean',
];

function assign(target, _varArgs) {
  'use strict';
  if (target === null || target === undefined) {
    throw new TypeError('Cannot convert undefined or null to object');
  }

  const to = Object(target);

  for (let index = 1; index < arguments.length; index++) {
    const nextSource = arguments[index];

    if (nextSource !== null && nextSource !== undefined) {
      for (const nextKey in nextSource) {
        if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
          to[nextKey] = nextSource[nextKey];
        }
      }
    }
  }
  return to;
}

/**
 *
 * @param options Quill static options. https://github.com/gtgalone/react-quilljs#options
 * @returns Returns quill, quillRef, and Quill. https://github.com/gtgalone/react-quilljs#return
 */
export const useQuill = (options = { theme, modules, formats }) => {
  const quillRef= useRef();
  const articleIdRef = useRef(options.articleId);
  const [obj, setObj] = useState({
    Quill: Quill,
    quillRef,
    quill: undefined,
    editorRef: quillRef,
    editor: undefined,
  });

  // Update the ref whenever articleId changes
  useEffect(() => {
    articleIdRef.current = options.articleId;
  }, [options.articleId]);

  const uploadFunc= file => {
    return new Promise((resolve, reject) => {
      const currentArticleId = articleIdRef.current;
      if (!currentArticleId) {
        reject("No articleId provided");
        return;
      }

      uploadFile(file, currentArticleId, 'article_media').then((fileId) => {
          getFileURL(fileId).then((urlResponse) => {
             resolve(urlResponse.url);
          })
      })
      .catch(error => {
        reject("Upload failed");
        console.error("Error:", error);
      });
    });
  }

  useEffect(() => {
    if (!obj.quill && quillRef && quillRef.current ) {
      const opts = {
        modules: {
          ...options.modules,
          clipboard: {
            matchVisual: false,
          },
        },
        formats: options.formats || formats,
        theme: options.theme || theme,
        placeholder: options.placeholder,
        bounds: options.bounds
      };

      // Always include image format, but conditionally add uploader
      if (options.articleId) {
        opts.modules.imageUploader = {
          upload: uploadFunc
        };
      }

      const quill = new obj.Quill(quillRef.current, opts);

      setObj(assign(assign({}, obj), { quill, editor: quill }));
    }
  }, [obj, options]);

  return obj;
};